import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { PageHead, Skel, Empty } from '../components/ui'

// Beneficial-ownership screening (part 156). The page is read-only: entities and edges are
// maintained through the API, and the auditor's job here is to read the structure and the
// signals the domain rules raised. Indirect stakes are computed server-side so the number
// on screen and the number in an export can never disagree.

type EntityKind = 'company' | 'person'

interface UBOEntity {
  id: string
  name: string
  kind: EntityKind
  tax_id?: string
  country?: string
  address_hash?: string
  created_at: string
}

interface UBOOwnership {
  id: string
  owner_entity_id: string
  owner_name: string
  owned_entity_id: string
  owned_name: string
  pct: number
  since?: string
  source?: string
}

interface UBOSignal {
  code: string
  severity: string
  entity_ids: string[]
  entity_names: string[]
  detail: string
}

interface UBOHolding {
  owner_entity_id: string
  owner_name: string
  pct: number
  depth: number
  direct: boolean
}

interface UBOEffective {
  root_id: string
  root_name: string
  max_depth: number
  holdings: UBOHolding[]
}

const kindLabel: Record<string, string> = { company: 'นิติบุคคล', person: 'บุคคลธรรมดา' }

const severityLabel: Record<string, string> = {
  critical: 'วิกฤต', high: 'สูง', medium: 'ปานกลาง', low: 'ต่ำ',
}

const signalLabel: Record<string, string> = {
  SHARED_ADDRESS_HASH: 'ใช้ที่อยู่เดียวกัน',
  SHARED_TAX_ID: 'เลขผู้เสียภาษีซ้ำกัน',
  ROUND_TRIP_OWNERSHIP: 'ถือหุ้นไขว้สองทาง',
  HIGH_CONCENTRATION: 'กระจุกตัวในผู้ถือหุ้นรายเดียว',
}

const pctText = (n: number) => n.toFixed(2)

const shortHash = (hash?: string) => {
  if (!hash) return '—'
  return hash.length > 10 ? `${hash.slice(0, 10)}…` : hash
}

const fetchEntities = () => api<UBOEntity[]>('/v1/ubo/entities')
const fetchEdges = () => api<UBOOwnership[]>('/v1/ubo/ownership')
const fetchSignals = () => api<UBOSignal[]>('/v1/ubo/signals')

export function Ownership() {
  const [rootId, setRootId] = useState('')

  const entitiesQ = useQuery({ queryKey: ['ubo', 'entities'], queryFn: fetchEntities, staleTime: 30_000 })
  const edgesQ = useQuery({ queryKey: ['ubo', 'ownership'], queryFn: fetchEdges, staleTime: 30_000 })
  const signalsQ = useQuery({ queryKey: ['ubo', 'signals'], queryFn: fetchSignals, staleTime: 30_000 })

  const effectiveQ = useQuery({
    queryKey: ['ubo', 'effective', rootId],
    queryFn: () => api<UBOEffective>(`/v1/ubo/effective?root_id=${encodeURIComponent(rootId)}`),
    enabled: Boolean(rootId),
  })

  const entities = entitiesQ.data ?? []
  const edges = edgesQ.data ?? []
  const signals = signalsQ.data ?? []
  const effective = effectiveQ.data
  const cycle = effectiveQ.error instanceof ApiError && effectiveQ.error.code === 'OWNERSHIP_CYCLE'

  const choose = (id: string) => {
    setRootId(id)
    document.getElementById('ubo-effective')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <>
      <PageHead
        title="โครงสร้างการถือหุ้น"
        sub="ตรวจสอบผู้ถือหุ้นที่แท้จริง · คำนวณสัดส่วนทางอ้อมได้ลึกสุด 6 ชั้น"
      />

      <div className="row" style={{ gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <span className="chip">นิติบุคคลและบุคคล {entities.length} ราย</span>
        <span className="chip">เส้นความสัมพันธ์ {edges.length} เส้น</span>
        <span className={`chip${signals.length > 0 ? ' gold' : ''}`}>สัญญาณที่พบ {signals.length} รายการ</span>
      </div>

      <div className="grid-main">
        <div className="card">
          <div className="card-pad" style={{ paddingBottom: 14 }}>
            <div className="card-label">ทะเบียนนิติบุคคลและบุคคล</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
              ระบบเก็บเฉพาะค่าแฮชของที่อยู่ ไม่เก็บที่อยู่จริง
            </div>
          </div>

          {entitiesQ.isLoading ? <div style={{ padding: 16 }}><Skel h={200} /></div> : entities.length === 0 ? (
            <Empty title="ยังไม่มีข้อมูลนิติบุคคล" body="เพิ่มนิติบุคคลผ่าน API ก่อนเริ่มการคัดกรองผู้ถือหุ้นที่แท้จริง" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>ชื่อ</th>
                    <th>ประเภท</th>
                    <th>ประเทศ</th>
                    <th>เลขผู้เสียภาษี</th>
                    <th>ที่อยู่ (แฮช)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entities.map((e) => (
                    <tr key={e.id} onClick={() => choose(e.id)}>
                      <td>{e.name}</td>
                      <td><span className="chip">{kindLabel[e.kind] ?? e.kind}</span></td>
                      <td className="dim">{e.country || '—'}</td>
                      <td className="mono">{e.tax_id || '—'}</td>
                      <td className="mono dim">{shortHash(e.address_hash)}</td>
                      <td className="num">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={(ev) => { ev.stopPropagation(); choose(e.id) }}
                        >
                          ดูโครงสร้าง
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">สัญญาณความเสี่ยง</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 6, marginBottom: 4 }}>
            กฎที่ใช้: ที่อยู่ซ้ำ · เลขผู้เสียภาษีซ้ำ · ถือหุ้นไขว้ · กระจุกตัวตั้งแต่ 50% ขึ้นไป 3 ชั้น
          </div>

          {signalsQ.isLoading ? <div style={{ marginTop: 12 }}><Skel h={160} /></div> : signals.length === 0 ? (
            <Empty title="ไม่พบสัญญาณความเสี่ยง" body="โครงสร้างนี้ยังไม่ตรงกับกฎการคัดกรองใด ๆ" />
          ) : (
            <div>
              {signals.map((s, i) => (
                <div
                  key={`${s.code}-${s.entity_ids.join('-')}`}
                  style={{
                    padding: '13px 0',
                    borderBottom: i === signals.length - 1 ? 'none' : '1px solid var(--color-hairline)',
                  }}
                >
                  <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13 }}>{signalLabel[s.code] ?? s.code}</span>
                    <span className={`sev sev-${s.severity}`}>
                      <i className="sev-dot" />
                      {severityLabel[s.severity] ?? s.severity}
                    </span>
                  </div>
                  <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 6 }}>
                    {s.detail}
                  </div>
                  <div className="mono dim" style={{ fontSize: 10.5, marginTop: 4 }}>{s.code}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-pad" style={{ paddingBottom: 14 }}>
          <div className="card-label">เส้นการถือหุ้น</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
            ทิศทางลูกศรคือ ผู้ถือหุ้น → ผู้ถูกถือหุ้น · หนึ่งคู่มีได้หนึ่งเส้น
          </div>
        </div>

        {edgesQ.isLoading ? <div style={{ padding: 16 }}><Skel h={160} /></div> : edges.length === 0 ? (
          <Empty title="ยังไม่มีเส้นการถือหุ้น" body="เมื่อบันทึกสัดส่วนการถือหุ้น โครงสร้างจะปรากฏที่นี่" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th>ผู้ถือหุ้น</th>
                  <th />
                  <th>ผู้ถูกถือหุ้น</th>
                  <th className="num">สัดส่วน</th>
                  <th>ตั้งแต่</th>
                  <th>แหล่งข้อมูล</th>
                </tr>
              </thead>
              <tbody>
                {edges.map((e) => (
                  <tr key={e.id}>
                    <td>{e.owner_name}</td>
                    <td className="dim" style={{ width: 24 }}>→</td>
                    <td>{e.owned_name}</td>
                    <td className="num tnum">{pctText(e.pct)}%</td>
                    <td className="dim">{e.since || '—'}</td>
                    <td className="dim">{e.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" id="ubo-effective" style={{ marginTop: 18 }}>
        <div className="card-label">ค้นหาผู้ถือหุ้นที่แท้จริง</div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
          เลือกนิติบุคคลเพื่อดูสัดส่วนทั้งทางตรงและทางอ้อมของผู้ถือหุ้นแต่ละราย
        </div>

        <div className="row" style={{ gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <select className="select" aria-label="เลือกนิติบุคคล" value={rootId} onChange={(ev) => setRootId(ev.target.value)} style={{ minWidth: 300 }}>
            <option value="">เลือกนิติบุคคล</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <span className="dim" style={{ fontSize: 11.5 }}>เพดานการคำนวณ 6 ชั้น</span>
        </div>

        {!rootId ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 16 }}>
            ยังไม่ได้เลือกนิติบุคคล
          </div>
        ) : effectiveQ.isPaused ? (
          <div className="form-error" style={{ marginTop: 16 }}>
            การเชื่อมต่อขัดข้อง จึงยังคำนวณสัดส่วนไม่ได้ กรุณาตรวจสอบเครือข่ายแล้วลองใหม่
          </div>
        ) : effectiveQ.isPending ? (
          <div style={{ marginTop: 16 }}><Skel h={120} /></div>
        ) : cycle ? (
          <div className="form-error" style={{ marginTop: 16 }}>
            พบวงจรการถือหุ้นในโครงสร้างนี้ จึงคำนวณสัดส่วนรวมไม่ได้ กรุณาตรวจสอบเส้นการถือหุ้นที่ถือหุ้นไขว้กัน
          </div>
        ) : effectiveQ.isError ? (
          <div className="form-error" style={{ marginTop: 16 }}>
            {(effectiveQ.error as Error).message}
          </div>
        ) : effective && effective.holdings.length === 0 ? (
          <div className="empty" style={{ padding: '28px 0' }}>
            <h3>ไม่พบผู้ถือหุ้นเหนือขึ้นไป</h3>
            <p style={{ fontSize: 12.5 }}>ไม่พบผู้ถือหุ้นในขอบเขต 6 ชั้นของ {effective.root_name}</p>
          </div>
        ) : effective ? (
          <>
            <div className="row" style={{ justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
              <span className="heading-s">{effective.root_name}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>
                พบผู้ถือหุ้น {effective.holdings.length} ราย · คำนวณถึง {effective.max_depth} ชั้น
              </span>
            </div>
            <table className="data" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>ผู้ถือหุ้น</th>
                  <th>เส้นทาง</th>
                  <th className="num">สัดส่วนที่มีผล</th>
                  <th style={{ width: 170 }}>เทียบเพดาน 100%</th>
                </tr>
              </thead>
              <tbody>
                {effective.holdings.map((h) => (
                  <tr key={h.owner_entity_id}>
                    <td>{h.owner_name}</td>
                    <td className="dim">{h.direct ? 'ทางตรง' : `ทางอ้อม ${h.depth} ชั้น`}</td>
                    <td className="num tnum">{pctText(h.pct)}%</td>
                    <td>
                      <span className="bar" style={{ display: 'block', width: '100%' }}>
                        <i style={{ width: `${Math.min(100, h.pct)}%` }} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.7 }}>
              สัดส่วนทางอ้อมคำนวณจากการคูณสัดส่วนตามเส้นทางและรวมทุกเส้นทางที่มาถึงนิติบุคคลนี้
              ตัวเลขที่เกิน 100% จะถูกปรับเพดานไว้ที่ 100%
            </div>
          </>
        ) : null}
      </div>
    </>
  )
}
